import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { money, fmtDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Undo } from "lucide-react";
import { toast } from "sonner";

export default function PurchaseReturn() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState({});
  const [reason, setReason] = useState("");

  const { data: purchases } = useQuery({ queryKey: ["pr-purchases", search], queryFn: async () => (await api.get("/purchases", { params: { search, period: "this_month" } })).data });
  const { data: returns } = useQuery({ queryKey: ["purchase-returns"], queryFn: async () => (await api.get("/purchase-returns", { params: { period: "this_month" } })).data });

  function selectPurchase(p) {
    setSelected(p);
    const init = {};
    p.items.forEach((it, i) => { init[i] = { checked: false, qty: it.qty }; });
    setItems(init);
  }

  const refund = selected ? selected.items.reduce((s, it, i) => items[i]?.checked ? s + (it.line_total / it.qty) * Number(items[i].qty || 0) : s, 0) : 0;

  async function submit() {
    const sel = selected.items.map((it, i) => ({ it, i })).filter(({ i }) => items[i]?.checked)
      .map(({ it, i }) => ({ product_id: it.product_id, name: it.name, qty: Number(items[i].qty), purchase_price: it.purchase_price, tax_rate: it.tax_rate }));
    if (!sel.length) { toast.error("Select items to return"); return; }
    try {
      await api.post("/purchase-returns", { purchase_id: selected.id, items: sel, refund_amount: refund, reason });
      toast.success("Purchase return processed · Stock reduced");
      setSelected(null); setReason("");
      qc.invalidateQueries({ queryKey: ["purchase-returns"] });
    } catch (e) { toast.error(apiError(e)); }
  }

  return (
    <div className="space-y-5">
      <div><h2 className="font-display text-2xl font-bold text-[#0F172A]">Purchase Return</h2>
        <p className="text-slate-500 text-sm">Return purchased items back to a vendor</p></div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-4 border-slate-200">
          <h3 className="font-display font-semibold text-[#1A2A5E] mb-3">1. Select Purchase</h3>
          <div className="relative mb-3"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search invoice / vendor..." className="pl-9" data-testid="pr-search" /></div>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {(purchases || []).map((p) => (
              <button key={p.id} onClick={() => selectPurchase(p)} className={`w-full text-left p-3 rounded-lg border ${selected?.id === p.id ? "border-[#F4571E] bg-[#FFF3EF]" : "border-slate-200 hover:border-slate-300"}`}>
                <div className="flex justify-between"><span className="font-mono font-medium text-[#1A2A5E]">{p.invoice_no || "No Invoice"}</span><span className="font-mono">{money(p.grand_total)}</span></div>
                <div className="text-xs text-slate-400">{p.vendor_name} · {fmtDateTime(p.created_at)}</div>
              </button>
            ))}
          </div>
        </Card>

        <Card className="p-4 border-slate-200">
          <h3 className="font-display font-semibold text-[#1A2A5E] mb-3">2. Items to Return</h3>
          {selected ? (
            <div className="space-y-3">
              {selected.items.map((it, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50">
                  <Checkbox checked={items[i]?.checked} onCheckedChange={(v) => setItems({ ...items, [i]: { ...items[i], checked: v } })} />
                  <div className="flex-1"><div className="text-sm font-medium">{it.name}</div><div className="text-xs text-slate-400">Qty: {it.qty} · {money(it.purchase_price)}</div></div>
                  <Input type="number" min={1} max={it.qty} value={items[i]?.qty || it.qty} disabled={!items[i]?.checked} onChange={(e) => setItems({ ...items, [i]: { ...items[i], qty: e.target.value } })} className="h-8 w-16 text-center font-mono" />
                </div>
              ))}
              <div><Label>Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" /></div>
              <div className="flex justify-between pt-2 border-t"><span className="font-display font-semibold">Refund</span><span className="font-mono font-bold text-xl text-[#F4571E]">{money(refund)}</span></div>
              <Button className="w-full bg-[#1A2A5E]" onClick={submit} data-testid="submit-purchase-return">Process Return</Button>
            </div>
          ) : <div className="text-center py-16 text-slate-400"><Undo className="h-8 w-8 mx-auto mb-2 opacity-40" />Select a purchase</div>}
        </Card>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <div className="p-4 border-b"><h3 className="font-display font-semibold text-[#0F172A]">Recent Purchase Returns</h3></div>
        <Table>
          <TableHeader><TableRow className="bg-slate-50"><TableHead>Invoice</TableHead><TableHead>Vendor</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Refund</TableHead></TableRow></TableHeader>
          <TableBody>
            {(returns || []).length ? returns.map((r) => (
              <TableRow key={r.id}><TableCell className="font-mono">{r.invoice_no || "—"}</TableCell><TableCell>{r.vendor_name}</TableCell><TableCell className="text-slate-500 text-sm">{fmtDateTime(r.created_at)}</TableCell><TableCell className="text-right font-mono font-semibold text-[#F4571E]">{money(r.refund_amount)}</TableCell></TableRow>
            )) : <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-400">No returns yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
